import {
  AccordionListContext,
  AccordionListFranchiseProjectStepsItem,
  AccordionListItem,
  AccordionListPriceExplanationItem,
  ListeAccordeon,
} from '@components/liste-accordeon/models';
import { imageMock, videoMock } from '@mock/schema/components/shared';
import { Block } from '@shared/models';

function getMockItems(context: AccordionListContext): AccordionListItem[] {
  switch (context) {
    case 'priceExplanation':
      return [
        {
          title: 'Les meubles',
          medias: [imageMock],
          text: `<strong>Types de meubles</strong><p>Le système d'ouverture fait varier le prix du <strong>meuble</strong>. Par exemple, un meuble avec 2 portes battantes est moins couteux qu'un meuble avec 2 coulissants.</p><p>Les dimensions du meuble vont également impacter le prix : une armoire coutera plus cher qu'un meuble bas !</p><strong>Façades</strong><p>Le type de matériaux fait varier le prix. Par exemple, une façade mélaminée coûte moins cher qu'une façade à cadre ou qu'une façade laquée.</p><p>Et pour la poignée, un large choix de modèles sans supplément de prix est disponible. Si vous optez pour un modèle de poignée monté en usine, prévoyez un surcoût !</p><strong>Accessoires</strong><p>Range-couverts, poubelles, éclairages… : plus vous accessoirisez votre meuble, plus son prix augmente.</p><strong>Plan de travail</strong><p>Le plan de travail représente une part importante du budget, il faut bien évaluer vos besoins et vos priorités pour choisir la bonne matière. Un plan stratifié sera bien plus économique qu'un <a href="/mock-blog-b2c/inspiration/style/plan-de-travail-en-quartz-pour-ma-cuisine-quels-avantages">plan Quartz</a> !</p>`,
          tipBlock: {},
        },
        {
          title: 'Les équipements',
          medias: [imageMock],
          text: `<strong>Electroménagers</strong><p>Le nombre d’électros, la marque, le type de fonctionnalité (pyrolyse, catalyse...), le mode d’intégration dans la cuisine (pose libre ou intégrée) sont tous des éléments qui font varier le prix d’un électro, quelques pistes pour optimiser son budget électro :</p><ul><li>privilégier la pose libre pour le frigo ou le micro-ondes pour éviter le coût des meubles d’intégration,</li><li>opter pour le bon niveau de fonction par rapport à vos besoins pour éviter de surinvestir. Par exemple, un four pyrolyse n’est pas nécessaire si on l’utilise peu son four.</li></ul><strong>Eviers</strong><p>La marque, le matériau (inox, résine, granit, céramique...) et la taille (1 ou 2 cuves, petite ou grande) font varier le prix de l’évier que vous allez choisir.</p><p>Son installation également : une cuve sous plan sera plus coûteuse qu’un évier à encastrement standard.</p><strong>Mitigeurs</strong><p>La marque, les fonctionnalités (avec ou sans douchette, rabattable...) et les finitions (inox, chromé, noir...) jouent sur le prix du mitigeur.</p>`,
          tipBlock: {
            title: 'L’astuce SoCoo’c',
            text: `Pensez au <a href="/mock-pack-eviers-mitigeurs">pack</a> pour les électroménagers, l'évier et le mitigeur !`,
          },
        },
        {
          title: 'Les services',
          medias: [imageMock],
          text: `<p>Pour estimer le budget complet de votre cuisine, pensez également à inclure les services SoCoo’c qui vont vous faciliter la vie !</p><strong>Métré</strong><p>Etape incontournable, le métré vérifie que la conception est bien adaptée à votre pièce, avant son départ en fabrication.</p><strong>Livraison</strong><p>Pratique, pas de mal au dos et on n’abîme pas la voiture !</p><strong>Pose</strong><p>Le nombre de meubles et donc le temps requis pour les poser, fait varier le prix de la pose</p>`,
          tipBlock: {
            title: 'L’astuce SoCoo’c',
            text: `Chez SoCoo'c, la pose est à la carte : <a href="/mock-la-pose/perso">posez vous-même</a> ou <a href="/mock-la-pose/pro">faites appel à un pro</a>. Choisissez la formule qui vous convient selon votre budget et votre expertise en bricolage !`,
            cta: {
              label: 'Se renseigner sur la pose',
              url: '/mock-la-pose',
            },
          },
        },
      ] as AccordionListPriceExplanationItem[];
    case 'franchiseProjectSteps':
    default:
      return [
        {
          title: 'On prend contact',
          medias: [videoMock(true)],
          text: `<ul><li>1ère prise de contact avec votre développeur via notre formulaire puis lors d'un échange téléphonique</li><li>Co-validation entre le profil du candidat et du choix de l'enseigne</li></ul>`,
          cta: {
            label: 'Nous contacter',
            url: 'mock-contact-b2b',
          },
        },
        {
          title: 'On se rencontre',
          medias: [imageMock],
          text: `<ul><li>Découverte du groupe lors d'une journée dédiée</li><li>Immersion de 3 jours en magasin</li><li>Confirmation mutuelle de votre candidature</li></ul>`,
        },
        {
          title: 'On se rencontre',
          medias: [videoMock(true), imageMock],
          text: `<ul><li>Recherche de votre local et établissement du prévisionnel</li><li>Lancement des travaux et agencement de votre magasin</li></ul>`,
        },
        {
          title: 'On construit votre équipe',
          medias: [videoMock(), { link: 'https://youtu.be/oavMtUWDBTM?si=S9zhXw8ZK7NIKjFr' }],
          text: `<ul><li>Recrutement de vos collaborateurs</li><li>Formation de toute votre équipe</li></ul>`,
        },
        {
          title: 'On ouvre',
          medias: [imageMock, videoMock(true)],
          text: `<ul><li>Ouverture de votre magasin</li><li>Accompagnement sur-mesure : aide au pilotage des indicateurs de performance et analyse de la satisfaction client, coaching personnalisé</li></ul>`,
        },
      ] as AccordionListFranchiseProjectStepsItem[];
  }
}

export const listeAccordeonMock = (context: AccordionListContext): Block<ListeAccordeon> => ({
  type: 'liste-accordeon',
  data: {
    context,
    items: getMockItems(context),
    gtmConfigItemsCta:
      context === 'franchiseProjectSteps'
        ? {
            event: 'click_cta_contact_franchise',
            group: 'contact',
            description: 'contact franchise',
            context: 'clic bouton vers formulaire franchise',
          }
        : undefined,
  },
});
